import { useEffect, useMemo, useState } from 'react'
import { AlertCircle, CheckCircle2, ChevronRight, Clock3, Loader2, Network as NetworkIcon, Search, XCircle } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { apiClient, type Job, type Network, type Validation } from '@/lib/api-client'
import { cn } from '@/lib/utils'

type ValidationInventoryItem = Validation & {
  networkId: string
  networkName: string
  networkSourceType: Network['sourceType']
  networkDefaultBranch?: string
  networkRepoLabel?: string
  jobCreatedAt?: string
}

type PageState = {
  networks: Network[]
  validations: ValidationInventoryItem[]
}

type InventoryStatus = 'passed' | 'failed' | 'in_progress' | 'queued' | 'unknown'

const STATUS_LABELS: Record<InventoryStatus, string> = {
  passed: 'Passed',
  failed: 'Failed',
  in_progress: 'Running',
  queued: 'Queued',
  unknown: 'Unknown',
}

const STATUS_BADGE_CLASSNAME: Record<InventoryStatus, string> = {
  passed: 'border-emerald-100 bg-emerald-50 text-emerald-700',
  failed: 'border-red-100 bg-red-50 text-red-700',
  in_progress: 'border-amber-100 bg-amber-50 text-amber-700',
  queued: 'border-slate-200 bg-slate-100 text-slate-700',
  unknown: 'border-border/60 bg-background text-foreground',
}

function formatValidationType(type?: string) {
  if (type === 'analysis') {
    return 'Analysis'
  }

  if (type === 'simulation') {
    return 'Simulation'
  }

  return 'Validation'
}

function formatStatusLabel(value?: string | null) {
  if (!value) {
    return 'Unknown'
  }

  return value
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (character) => character.toUpperCase())
}

function formatShortId(value?: string | null) {
  if (!value) {
    return 'UNKNOWN'
  }

  return value.slice(-6).toUpperCase()
}

function getValidationActivityAt(validation: ValidationInventoryItem) {
  return validation.completedAt || validation.startedAt || validation.createdAt || validation.jobCreatedAt || null
}

function formatRelativeTime(value?: string | null) {
  if (!value) {
    return 'No recorded activity'
  }

  const date = new Date(value)
  if (Number.isNaN(date.getTime())) {
    return 'No recorded activity'
  }

  const diffMs = date.getTime() - Date.now()
  const diffMinutes = Math.round(diffMs / (1000 * 60))
  const formatter = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' })

  if (Math.abs(diffMinutes) < 60) {
    return formatter.format(diffMinutes, 'minute')
  }

  const diffHours = Math.round(diffMinutes / 60)
  if (Math.abs(diffHours) < 48) {
    return formatter.format(diffHours, 'hour')
  }

  return formatter.format(Math.round(diffHours / 24), 'day')
}

function getDurationSeconds(validation: ValidationInventoryItem) {
  const start = validation.startedAt || validation.createdAt
  const end = validation.completedAt

  if (!start) {
    return null
  }

  const startMs = new Date(start).getTime()
  const endMs = new Date(end || Date.now()).getTime()

  if (Number.isNaN(startMs) || Number.isNaN(endMs) || endMs < startMs) {
    return null
  }

  return Math.round((endMs - startMs) / 1000)
}

function formatDuration(seconds: number | null) {
  if (seconds === null) {
    return 'Duration unavailable'
  }

  if (seconds < 60) {
    return `${seconds}s`
  }

  const minutes = Math.floor(seconds / 60)
  const remainingSeconds = seconds % 60

  if (minutes < 60) {
    return `${minutes}m ${remainingSeconds}s`
  }

  const hours = Math.floor(minutes / 60)
  const remainingMinutes = minutes % 60
  return `${hours}h ${remainingMinutes}m`
}

function getInventoryStatus(validation: ValidationInventoryItem): InventoryStatus {
  if (['running', 'pending'].includes(validation.status)) {
    return 'in_progress'
  }

  if (validation.status === 'queued') {
    return 'queued'
  }

  if (validation.result === 'failure' || ['failed', 'failure', 'error', 'errored', 'cancelled'].includes(validation.status)) {
    return 'failed'
  }

  if (validation.result === 'success' || ['completed', 'success', 'passed'].includes(validation.status)) {
    return 'passed'
  }

  return 'unknown'
}

function getStatusMeta(validation: ValidationInventoryItem) {
  const status = getInventoryStatus(validation)

  if (status === 'in_progress') {
    return {
      key: status,
      label: 'Running',
      icon: Loader2,
      iconClassName: 'text-amber-600',
      summary: `Running for ${formatDuration(getDurationSeconds(validation))}`,
    }
  }

  if (status === 'queued') {
    return {
      key: status,
      label: 'Queued',
      icon: Clock3,
      iconClassName: 'text-slate-500',
      summary: `Queued ${formatRelativeTime(validation.createdAt || validation.jobCreatedAt)}`,
    }
  }

  if (status === 'failed') {
    return {
      key: status,
      label: 'Failed',
      icon: XCircle,
      iconClassName: 'text-red-600',
      summary: `Failed ${formatRelativeTime(getValidationActivityAt(validation))}`,
    }
  }

  if (status === 'passed') {
    return {
      key: status,
      label: 'Passed',
      icon: CheckCircle2,
      iconClassName: 'text-emerald-600',
      summary: `Completed ${formatRelativeTime(getValidationActivityAt(validation))}`,
    }
  }

  return {
    key: status,
    label: formatStatusLabel(validation.status),
    icon: AlertCircle,
    iconClassName: 'text-muted-foreground',
    summary: formatRelativeTime(getValidationActivityAt(validation)),
  }
}

function getNetworkRepoLabel(network: Network) {
  if (network.sourceType === 'github') {
    const config = network.sourceConfig as { fullName?: string }
    return config.fullName || network.name
  }

  if (network.sourceType === 'http') {
    const config = network.sourceConfig as { url?: string }
    return config.url || network.name
  }

  if (network.sourceType === 'local') {
    const config = network.sourceConfig as { path?: string }
    return config.path || network.name
  }

  return network.name
}

function getSourceLabel(validation: ValidationInventoryItem) {
  if (validation.networkSourceType === 'github') {
    return validation.networkRepoLabel || 'GitHub'
  }

  if (validation.networkSourceType === 'http') {
    return 'HTTP source'
  }

  if (validation.networkSourceType === 'local') {
    return 'Local source'
  }

  if (validation.networkSourceType === 'cli') {
    return 'CLI submission'
  }

  if (validation.networkSourceType === 'direct') {
    return 'Direct upload'
  }

  return formatStatusLabel(validation.networkSourceType)
}

function getTypeCounts(validations: ValidationInventoryItem[]) {
  const counts = new Map<string, number>()

  validations.forEach((validation) => {
    const label = formatValidationType(validation.type)
    counts.set(label, (counts.get(label) ?? 0) + 1)
  })

  return Array.from(counts.entries()).sort((left, right) => right[1] - left[1])
}

export default function ValidationsPage() {
  const navigate = useNavigate()
  const [data, setData] = useState<PageState>({ networks: [], validations: [] })
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<'all' | InventoryStatus>('all')
  const [networkFilter, setNetworkFilter] = useState('all')

  useEffect(() => {
    void loadValidations()
  }, [])

  const loadValidations = async () => {
    try {
      setIsLoading(true)
      setError(null)

      const networksResponse = await apiClient.listNetworks()
      const networks = networksResponse.success ? networksResponse.data?.networks ?? [] : []

      const jobResponses = await Promise.all(
        networks.map(async (network) => {
          const response = await apiClient.getNetworkJobs(network.id)
          const jobs = response.success ? response.data?.jobs ?? [] : []

          return jobs.flatMap((job: Job) =>
            (job.validations ?? []).map((validation) => ({
              ...validation,
              networkId: network.id,
              networkName: network.name,
              networkSourceType: network.sourceType,
              networkDefaultBranch: network.defaultBranch,
              networkRepoLabel: getNetworkRepoLabel(network),
              jobCreatedAt: job.createdAt,
            })),
          )
        }),
      )

      const validations = jobResponses
        .flat()
        .sort((left, right) => new Date(getValidationActivityAt(right) || 0).getTime() - new Date(getValidationActivityAt(left) || 0).getTime())

      setData({ networks, validations })
    } catch (loadError) {
      console.error('Failed to load validations inventory:', loadError)
      setError('Failed to load validations inventory')
    } finally {
      setIsLoading(false)
    }
  }

  const filteredValidations = useMemo(() => {
    const normalizedSearchTerm = searchTerm.trim().toLowerCase()

    return data.validations.filter((validation) => {
      const matchesStatus = statusFilter === 'all' || getInventoryStatus(validation) === statusFilter
      const matchesNetwork = networkFilter === 'all' || validation.networkId === networkFilter
      const matchesSearch = !normalizedSearchTerm || [
        validation.networkName,
        validation.networkRepoLabel,
        validation.id,
        validation.jobId,
        validation.type,
        validation.networkDefaultBranch,
      ]
        .filter(Boolean)
        .some((value) => value?.toLowerCase().includes(normalizedSearchTerm))

      return matchesStatus && matchesNetwork && matchesSearch
    })
  }, [data.validations, networkFilter, searchTerm, statusFilter])

  const counts = useMemo(() => filteredValidations.reduce(
    (accumulator, validation) => {
      const status = getInventoryStatus(validation)
      accumulator.total += 1
      accumulator[status] += 1
      return accumulator
    },
    { total: 0, passed: 0, failed: 0, in_progress: 0, queued: 0, unknown: 0 },
  ), [filteredValidations])

  const failureNetworks = useMemo(() => {
    const failures = filteredValidations.filter((validation) => getInventoryStatus(validation) === 'failed')
    const summaries = new Map<string, { networkId: string; networkName: string; count: number; latestFailureAt: string | null }>()

    failures.forEach((validation) => {
      const current = summaries.get(validation.networkId)
      const failureAt = getValidationActivityAt(validation)

      if (!current) {
        summaries.set(validation.networkId, {
          networkId: validation.networkId,
          networkName: validation.networkName,
          count: 1,
          latestFailureAt: failureAt,
        })
        return
      }

      current.count += 1
      if ((failureAt || '') > (current.latestFailureAt || '')) {
        current.latestFailureAt = failureAt
      }
    })

    return Array.from(summaries.values()).sort((left, right) => right.count - left.count).slice(0, 5)
  }, [filteredValidations])

  const typeCounts = useMemo(() => getTypeCounts(filteredValidations), [filteredValidations])

  return (
    <div className="mx-auto w-full max-w-6xl space-y-8">
      <section className="space-y-4">
        <div className="flex flex-col gap-2 text-xs text-muted-foreground sm:flex-row sm:items-center">
          <span>Overview</span>
          <ChevronRight className="hidden h-4 w-4 sm:block" />
          <span className="font-semibold text-foreground">Validations</span>
        </div>
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="text-3xl font-bold tracking-[-0.04em] text-foreground">Validations</h1>
              <Badge variant="outline" className="border-border/50 bg-muted text-[10px] font-bold uppercase tracking-[0.16em] text-muted-foreground">
                {counts.in_progress > 0 ? `${counts.in_progress} active` : 'History'}
              </Badge>
            </div>
            <p className="max-w-3xl text-sm leading-6 text-muted-foreground">
              Operator validation history across connected networks, with dense run state, outcome, and timing details.
            </p>
          </div>
        </div>
      </section>

      {isLoading ? (
        <div className="flex min-h-[18rem] items-center justify-center rounded-xl border border-border bg-background">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : error ? (
        <section className="rounded-xl border border-red-100 bg-red-50 px-5 py-4 text-sm text-red-700">
          <div className="flex items-center gap-2">
            <AlertCircle className="h-4 w-4" />
            <span>{error}</span>
          </div>
        </section>
      ) : (
        <>
          <section className="space-y-4">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
              <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
                <label className="relative">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                  <input
                    type="search"
                    aria-label="Search validations"
                    value={searchTerm}
                    onChange={(event) => setSearchTerm(event.target.value)}
                    placeholder="Search validations..."
                    className="h-9 w-full rounded-md border border-border bg-background pl-9 pr-3 text-sm outline-none transition focus:border-primary sm:w-64"
                  />
                </label>
                <select
                  aria-label="Filter by network"
                  value={networkFilter}
                  onChange={(event) => setNetworkFilter(event.target.value)}
                  className="h-9 rounded-md border border-border bg-background px-3 text-sm outline-none transition focus:border-primary"
                >
                  <option value="all">All networks</option>
                  {data.networks.map((network) => (
                    <option key={network.id} value={network.id}>
                      {network.name}
                    </option>
                  ))}
                </select>
                <select
                  aria-label="Filter by status"
                  value={statusFilter}
                  onChange={(event) => setStatusFilter(event.target.value as 'all' | InventoryStatus)}
                  className="h-9 rounded-md border border-border bg-background px-3 text-sm outline-none transition focus:border-primary"
                >
                  <option value="all">All statuses</option>
                  <option value="in_progress">Running</option>
                  <option value="failed">Failed</option>
                  <option value="passed">Passed</option>
                  <option value="queued">Queued</option>
                  <option value="unknown">Unknown</option>
                </select>
              </div>
              <p className="text-sm text-muted-foreground">
                Showing <strong className="text-foreground">{counts.total}</strong> validation{counts.total === 1 ? '' : 's'}
              </p>
            </div>

            <Card className="overflow-hidden rounded-xl border border-border bg-background">
              <CardHeader className="border-b border-border bg-muted px-4 py-3 sm:px-5">
                <div className="flex flex-wrap items-center gap-4 text-xs font-semibold text-muted-foreground">
                  {(['passed', 'failed', 'in_progress', 'queued'] as const).map((key) => {
                    const label = STATUS_LABELS[key]
                    const value = counts[key]
                    const Icon = key === 'passed' ? CheckCircle2 : key === 'failed' ? XCircle : key === 'in_progress' ? Loader2 : Clock3

                    return (
                      <div key={key} className="flex items-center gap-2">
                        <Icon className={cn('h-3.5 w-3.5', key === 'passed' && 'text-emerald-600', key === 'failed' && 'text-red-600', key === 'in_progress' && 'animate-spin text-amber-600', key === 'queued' && 'text-slate-500')} />
                        <span>{value} {label}</span>
                      </div>
                    )
                  })}
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {filteredValidations.length === 0 ? (
                  <div className="px-5 py-12 text-sm text-muted-foreground">
                    No validations match the current filters.
                  </div>
                ) : (
                  <div className="divide-y divide-[#dce3ea]/80">
                    {filteredValidations.map((validation) => {
                      const status = getStatusMeta(validation)
                      const StatusIcon = status.icon

                      return (
                        <button
                          key={validation.id}
                          type="button"
                          className="flex w-full items-start justify-between gap-4 px-4 py-3 text-left transition-colors hover:bg-muted/50 sm:px-5"
                          onClick={() => navigate(`/validations/${validation.id}`)}
                        >
                          <div className="flex min-w-0 flex-1 gap-3">
                            <div className="pt-0.5">
                              <StatusIcon className={cn('h-4.5 w-4.5 shrink-0', status.iconClassName, status.key === 'in_progress' && 'animate-spin')} />
                            </div>
                            <div className="min-w-0 space-y-1">
                              <div className="flex min-w-0 flex-wrap items-center gap-2">
                                <span className="truncate text-sm font-semibold tracking-[-0.02em] text-primary">
                                  {validation.networkName} {formatValidationType(validation.type)}
                                </span>
                                <span className="text-xs text-muted-foreground">#{formatShortId(validation.id)}</span>
                              </div>
                              <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                                <span className="inline-flex items-center gap-1">
                                  <NetworkIcon className="h-3.5 w-3.5" />
                                  {validation.networkName}
                                </span>
                                <span>{getSourceLabel(validation)}</span>
                                <span>{validation.networkDefaultBranch || 'default branch'}</span>
                                <span>Job #{formatShortId(validation.jobId)}</span>
                              </div>
                            </div>
                          </div>
                          <div className="shrink-0 text-right">
                            <div className={cn('text-xs font-semibold', status.key === 'failed' && 'text-red-600', status.key === 'in_progress' && 'text-amber-700', status.key === 'passed' && 'text-foreground', status.key === 'queued' && 'text-muted-foreground')}>
                              {status.summary}
                            </div>
                            <div className="mt-1 flex items-center justify-end gap-2">
                              <Badge variant="outline" className={cn('text-[10px] font-semibold uppercase tracking-[0.12em]', STATUS_BADGE_CLASSNAME[status.key])}>
                                {status.label}
                              </Badge>
                            </div>
                            <div className="mt-1 text-[11px] text-muted-foreground">
                              {formatDuration(getDurationSeconds(validation))}
                            </div>
                          </div>
                        </button>
                      )
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </section>

          {(failureNetworks.length > 0 || typeCounts.length > 0) ? (
            <section className="grid gap-5 lg:grid-cols-[minmax(0,1.35fr)_minmax(18rem,0.85fr)]">
              {failureNetworks.length > 0 ? (
                <Card className="rounded-xl border border-border bg-background">
                  <CardHeader>
                    <CardTitle className="text-lg">Recent failure follow-up</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {failureNetworks.map((network) => (
                      <button
                        key={network.networkId}
                        type="button"
                        className="flex w-full items-center justify-between rounded-lg border border-border px-4 py-3 text-left transition-colors hover:bg-muted/50"
                        onClick={() => setNetworkFilter(network.networkId)}
                      >
                        <div className="min-w-0">
                          <p className="truncate text-sm font-semibold text-foreground">{network.networkName}</p>
                          <p className="text-xs text-muted-foreground">
                            Latest failed validation {formatRelativeTime(network.latestFailureAt)}
                          </p>
                        </div>
                        <Badge variant="outline" className="border-red-100 bg-red-50 text-[10px] font-semibold uppercase tracking-[0.12em] text-red-700">
                          {network.count} failed
                        </Badge>
                      </button>
                    ))}
                  </CardContent>
                </Card>
              ) : null}

              {typeCounts.length > 0 ? (
                <Card className="rounded-xl border border-border bg-background">
                  <CardHeader>
                    <CardTitle className="text-lg">Validation mix</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {typeCounts.slice(0, 5).map(([label, count]) => (
                      <div key={label} className="space-y-1">
                        <div className="flex items-center justify-between gap-3 text-sm">
                          <span className="font-medium text-foreground">{label}</span>
                          <span className="text-muted-foreground">{count}</span>
                        </div>
                        <div className="h-2 rounded-full bg-muted">
                          <div
                            className="h-2 rounded-full bg-primary"
                            style={{ width: `${Math.max((count / Math.max(filteredValidations.length, 1)) * 100, 8)}%` }}
                          />
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              ) : null}
            </section>
          ) : null}
        </>
      )}
    </div>
  )
}
